import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ClipCard } from "@/components/ClipCard";
import { BatchCompletionMark } from "@/components/BatchCompletionMark";
import { BatchDownload } from "@/components/BatchDownload";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";
import { Sparkles, AlertTriangle, ArrowLeft, Lock, MoonStar } from "lucide-react";

const NEON_BG = "https://images.unsplash.com/photo-1550275994-cdc89cd1948f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG5lb24lMjBsaWdodCUyMHdhdmVzJTIwZmx1aWR8ZW58MHx8fHwxNzkwMDczMTg2fDA&ixlib=rb-4.1.0&q=85";

export function Processing() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, openAuth } = useAuth();
  const [project, setProject] = useState(null);
  const [clips, setClips] = useState([]);
  const [completionProject, setCompletionProject] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    setProject(null);
    setClips([]);
    setCompletionProject(null);
  }, [projectId]);

  useEffect(() => {
    let active = true, previousStatus = null, failures = 0;
    const controller = new AbortController();
    setConnectionError(null);
    const poll = async () => {
      let delay = 2500;
      try {
        const { data } = await api.get(`/projects/${projectId}`, { timeout: 15000, signal: controller.signal });
        if (!active) return;
        failures = 0;
        setConnectionError(null);
        if (data.project.status === "completed") {
          let seen = false;
          try {
            const key = `cg_completion_seen:${projectId}`;
            seen = sessionStorage.getItem(key) === "1";
            sessionStorage.setItem(key, "1");
          } catch {}
          if (previousStatus && previousStatus !== "completed" && previousStatus !== "failed" && !seen) {
            setCompletionProject(projectId);
          }
        }
        previousStatus = data.project.status;
        setProject(data.project);
        setClips(data.clips || []);
        if (["completed", "failed"].includes(data.project.status)) return;
        if (data.project.processing_stalled) delay = 10000;
      } catch (error) {
        if (!active) return;
        const status = error.response?.status;
        const permanent = [401, 403, 404].includes(status);
        setConnectionError(permanent
          ? "This project could not be opened. Check the link and your account, then try again."
          : "Connection interrupted. Reconnecting automatically—keep this page open. Your saved clips remain available below.");
        if (permanent) return;
        failures += 1;
        delay = Math.min(30000, 2500 * 2 ** Math.min(failures, 4));
      }
      if (active) timer.current = setTimeout(poll, delay);
    };
    poll();
    return () => { active = false; controller.abort(); clearTimeout(timer.current); };
  }, [projectId, refresh]);

  const status = project?.status;
  const done = status === "completed";
  const failed = status === "failed";
  const stalled = !done && !failed && Boolean(project?.processing_stalled);
  const working = !done && !failed && !stalled;

  return (
    <div className="min-h-screen pt-24 px-5 sm:px-8 pb-20">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate(user ? "/dashboard" : "/")}
          className="flex items-center gap-2 text-sm text-copy-500 hover:text-foreground mb-8" data-testid="processing-back-btn">
          <ArrowLeft className="h-4 w-4" /> {user ? "Dashboard" : "Home"}
        </button>

        {connectionError && (
          <div role="alert" data-testid="processing-connection-error" className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm">{connectionError}</p>
            <Button data-testid="processing-reconnect-btn" variant="outline" onClick={() => setRefresh((value) => value + 1)}>Check again</Button>
          </div>
        )}

        {project?.processing_warning && !done && !failed && !stalled && (
          <div data-testid="processing-worker-warning" role="alert" className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-5 mb-6 text-sm">
            {project.processing_warning}
          </div>
        )}

        {stalled && <section data-testid="processing-stalled" role="alert" className="mx-auto my-10 max-w-2xl rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6 sm:p-8">
          <AlertTriangle aria-hidden="true" className="mb-4 h-7 w-7 text-amber-500" />
          <h2 data-testid="processing-stalled-title" className="font-display text-lg font-semibold">Processing stopped responding</h2>
          <p data-testid="processing-worker-warning" className="mt-3 text-sm leading-relaxed text-copy-300">{project.processing_warning}</p>
          <p data-testid="processing-last-progress" className="mt-4 break-words text-xs text-copy-500">{`Last recorded: ${project.stage || project.status} · ${project.progress || 0}%`}</p>
          {clips.length > 0 && <p data-testid="processing-stalled-saved" className="mt-3 text-sm text-cyan-400">{`${clips.length} saved clip${clips.length === 1 ? ' is' : 's are'} still available below.`}</p>}
          <Button data-testid="processing-check-status" variant="outline" onClick={() => setRefresh((value) => value + 1)} className="mt-5 rounded-full border-amber-500/40">Check status</Button>
        </section>}

        {working && (project || !connectionError) && (
          <div className="max-w-xl mx-auto text-center py-16" data-testid="processing-state">
            <div className="relative rounded-3xl border border-surface-800 overflow-hidden p-10">
              <img src={NEON_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 blur-xl scale-110" />
              <div className="absolute top-0 inset-x-0 h-0.5 overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-transparent via-cyan-400 to-transparent beam" />
              </div>
              <div className="relative z-10">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  className="mx-auto h-16 w-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-6">
                  <Sparkles className="h-7 w-7 text-cyan-400 pulse-glow" />
                </motion.div>
                <h2 data-testid="processing-stage" className="font-display text-2xl font-bold">{project?.stage || "Getting started"}</h2>
                <p data-testid="processing-explanation" className="text-sm text-copy-400 mt-2">{project?.status === "clipping" ? "Clips appear below as soon as each one is saved. Larger batches take longer." : "Preparing your video, transcribing speech and selecting your requested clips."}</p>
                <div className="mt-6">
                  <Progress data-testid="processing-progress" value={project?.progress || 5} className="h-2 bg-surface-800" />
                  <p data-testid="processing-percentage" className="text-xs text-copy-600 mt-2">{project?.progress || 5}%</p>
                </div>
                {clips.length > 0 && (
                  <p data-testid="processing-ready-count" className="text-sm text-cyan-400 mt-4">{clips.length} clip{clips.length > 1 ? "s" : ""} ready to view or download below</p>
                )}
                {project?.background_processing && (
                  <div data-testid="processing-safe-to-close" className="mt-6 rounded-xl border border-cyan-500/30 bg-cyan-500/[0.06] px-4 py-3 text-left">
                    <p className="text-sm font-medium text-cyan-300 flex items-center gap-2"><MoonStar className="h-4 w-4" /> Safe to close this page</p>
                    <p className="text-xs text-copy-400 mt-1 leading-relaxed">Your video is saved and clips are being created on our servers. Shut your laptop or get on with other things — {user ? (user.notify_on_complete === false ? "your clips will be waiting in your dashboard." : `we'll email ${user.email} when they're ready.`) : "come back to this link any time to collect them."}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {failed && (
          <div className="max-w-xl mx-auto text-center py-16" data-testid="processing-failed">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-5">
              <AlertTriangle className="h-6 w-6 text-red-400" />
            </div>
            <h2 className="font-display text-2xl font-bold">We hit a snag</h2>
            <p data-testid="processing-failure-message" className="text-sm text-copy-400 mt-2">{project?.error || "Something went wrong while processing your video."}</p>
            {clips.length > 0 && <p data-testid="processing-partial-notice" className="text-sm text-copy-300 mt-3">Your {clips.length} saved clip{clips.length === 1 ? " is" : "s are"} still available below. Only saved clips count toward your allowance.</p>}
            <Button data-testid="processing-another-video-btn" onClick={() => navigate(user ? "/dashboard" : "/")} className="mt-6 rounded-full bg-cyan-500 text-black hover:bg-cyan-400 font-semibold">
              Try another video
            </Button>
          </div>
        )}

        {(done || clips.length > 0) && (
          <div data-testid="results-view">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div className="flex items-start gap-4">
                {done && <BatchCompletionMark key={projectId} justFinished={completionProject === projectId} />}
                <div className="min-w-0">
                  <p data-testid={done ? "batch-complete-message" : "partial-results-message"} role="status" className="text-xs uppercase tracking-[0.25em] text-cyan-400 mb-2">{done ? "Ready to post" : failed ? "Saved clips · batch incomplete" : stalled ? "Saved clips · progress stopped" : "Ready while we work"}</p>
                  <h1 data-testid="results-title" className="font-display text-4xl font-black tracking-tighter">{project?.is_style_review ? "Your five-style comparison" : `${clips.length} clip${clips.length === 1 ? "" : "s"} ${done ? "generated" : "ready"}`}</h1>
                  <p data-testid="processing-source-filename" className="text-sm text-copy-500 mt-1 truncate max-w-md">from {project?.filename}</p>
                </div>
              </div>
              <BatchDownload projectId={projectId} readyCount={clips.length} />
            </div>

            {project?.watermarked && (
              <div className="rounded-2xl border border-cyan-500/30 bg-cyan-500/[0.05] p-5 mb-8 flex flex-wrap items-center justify-between gap-4"
                data-testid="upgrade-banner">
                <div className="flex items-center gap-3">
                  <Lock className="h-5 w-5 text-cyan-400" />
                  <p className="text-sm text-copy-300">These free clips are watermarked. Create an account for watermark-free exports & more clips.</p>
                </div>
                {!user && (
                  <Button data-testid="upgrade-signup-btn" onClick={() => openAuth("register")}
                    className="rounded-full bg-cyan-500 text-black hover:bg-cyan-400 font-semibold">Create free account</Button>
                )}
              </div>
            )}

            {project?.quality_note && <div data-testid="results-quality-note" className="rounded-xl border border-surface-800 bg-surface-950 px-5 py-4 mb-6 text-sm text-copy-300 leading-relaxed">{project.quality_note}</div>}
            <div className={`grid grid-cols-1 min-[460px]:grid-cols-2 sm:grid-cols-3 ${project?.is_style_review ? "xl:grid-cols-5" : "lg:grid-cols-4"} gap-4`}>
              {clips.map((c, i) => <ClipCard key={c.id} clip={c} index={i} />)}
            </div>

            <div className="text-center mt-12">
              <Button data-testid="processing-results-back-btn" onClick={() => navigate(user ? "/dashboard" : "/")}
                className="rounded-full bg-surface-100 text-black hover:bg-white font-semibold px-6">
                {user ? "Back to dashboard" : "Clip another video"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
